import {SubstitutionInquiryState} from '@/enums/substitution-manager/SubstitutionInquiryState';
import {InquiryRef, LegendEntry, LessonStatusInfo, LessonStatusKind, LogState, Tone} from '@/helpers/timetable2/types';

/**
 * Status of one lesson occurrence (pure, shared by tiles, list, resource views and the side panel).
 *
 * Scheduler flags (shrd WeekCalendar.toVuetify):
 * - isCanceled is only set by holidays (cancellationReason may be overwritten by VACANCY when the lesson is also vacant)
 * - isVacant = teacher null and owningTeacher set (substitution inquiry open, expired or cancellation)
 * - isSubstituted = teacher differs from owningTeacher
 * The substitution inquiry decides between "Ausfall" (cancellation configured / inquiry canceled)
 * and "Vertretung gesucht" (open) / "Unbesetzt" (no or expired inquiry).
 *
 * A ghost of a moved-away lesson (lessonMove.role 'origin', isCanceled + MOVED) is 'moved': it never counts as a lesson.
 *
 * Precedence: Verschoben (ghost) > Ferien > Ausfall > Vertretung gesucht / Unbesetzt > Vertretung > Entwurf > regulär
 */

export interface LessonStatusInput {
    /** Ghost of a lesson that was moved to another date */
    isMovedOrigin?: boolean;
    /** "Verschoben auf 19.10., 15:00" */
    movedText?: string;
    isCanceled?: boolean;
    isVacant?: boolean;
    isSubstituted?: boolean;
    isDraft?: boolean;
    inquiry?: Pick<InquiryRef, 'state' | 'cancellationConfigured'>;
    /** Optional cancellation reason label ("Fällt aus: Krankheit", same as Today / cancellationHeadline) */
    cancellationReasonText?: string;
}

const STATUS: Record<LessonStatusKind, Omit<LessonStatusInfo, 'text'> & { text: string }> = {
    moved: {kind: 'moved', text: 'Verschoben', icon: 'mdi-calendar-arrow-right', tone: 'muted', problem: false},
    holiday: {kind: 'holiday', text: 'Ferien', icon: 'mdi-palm-tree', tone: 'warn', problem: false},
    canceled: {kind: 'canceled', text: 'Ausfall', icon: 'mdi-cancel', tone: 'muted', problem: false},
    vacant: {kind: 'vacant', text: 'Unbesetzt', icon: 'mdi-account-alert', tone: 'bad', problem: true},
    substituted: {kind: 'substituted', text: 'Vertretung', icon: 'mdi-account-switch', tone: 'purple', problem: false},
    draft: {kind: 'draft', text: 'Entwurf', icon: 'mdi-pencil-outline', tone: 'muted', problem: false},
    regular: {kind: 'regular', text: 'Findet statt', icon: 'mdi-check-circle-outline', tone: 'info', problem: false},
};

/** Whether the inquiry stands for a cancellation (Ausfall) instead of a search for a substitute */
export function isCancellationInquiry(inquiry?: Pick<InquiryRef, 'state' | 'cancellationConfigured'>): boolean {
    return !!inquiry && (inquiry.cancellationConfigured || inquiry.state === SubstitutionInquiryState.CANCELED);
}

/** Whether a substitute is still being searched (open inquiry or substitutes available) */
export function isSearchingInquiry(inquiry?: Pick<InquiryRef, 'state' | 'cancellationConfigured'>): boolean {
    return !!inquiry && !isCancellationInquiry(inquiry)
        && (inquiry.state === SubstitutionInquiryState.OPEN || inquiry.state === SubstitutionInquiryState.SUBSTITUTE_AVAILABLE);
}

/** The occurrence does not take place here (ghost of a moved lesson, holiday, Ausfall): no lesson, no conflicts, no counts */
export function isOffKind(kind: LessonStatusKind): boolean {
    return kind === 'moved' || kind === 'holiday' || kind === 'canceled';
}

export function deriveLessonStatusKind(input: LessonStatusInput): LessonStatusKind {
    if (input.isMovedOrigin) {
        return 'moved';
    }
    if (input.isCanceled) {
        return 'holiday';
    }
    if (input.isVacant) {
        return isCancellationInquiry(input.inquiry) ? 'canceled' : 'vacant';
    }
    if (input.isSubstituted) {
        return 'substituted';
    }
    if (input.isDraft) {
        return 'draft';
    }
    return 'regular';
}

export function deriveLessonStatus(input: LessonStatusInput): LessonStatusInfo {
    const kind = deriveLessonStatusKind(input);
    const base = STATUS[kind];
    let text = base.text;
    let icon = base.icon;
    if (kind === 'vacant' && isSearchingInquiry(input.inquiry)) {
        text = 'Vertretung gesucht';
        icon = 'mdi-account-clock';
    }
    if (kind === 'canceled' && input.cancellationReasonText) {
        text = `Fällt aus: ${input.cancellationReasonText}`;
    }
    if (kind === 'moved' && input.movedText) {
        text = input.movedText;
    }
    return {...base, text, icon};
}

export interface LogStatusInfo {
    text: string;
    icon: string;
    tone: Tone;
    problem: boolean;
}

/** Display of the log state of an occurrence (undefined: nothing to show) */
export function logStatusInfo(state: LogState, logged: number, expected: number): LogStatusInfo | undefined {
    switch (state) {
        case 'complete':
            return {text: 'protokolliert', icon: 'mdi-check', tone: 'good', problem: false};
        case 'partial': {
            const missing = Math.max(0, expected - logged);
            return {
                text: missing === 1 ? '1 Protokoll fehlt' : `${missing} Protokolle fehlen`,
                icon: 'mdi-alert-circle-outline', tone: 'bad', problem: true,
            };
        }
        case 'missing':
            return {text: 'Protokoll fehlt', icon: 'mdi-alert-circle-outline', tone: 'bad', problem: true};
        default:
            return undefined;
    }
}

/** Tone class of modern.css */
export function toneClass(tone: Tone): string {
    return `tone-${tone}`;
}

export const OPEN_FOR_REGISTRATION: LegendEntry = {
    key: 'open', text: 'offen für Anmeldungen', icon: 'mdi-account-plus', tone: 'teal',
    description: 'Der Kurs ist offen für Neuanmeldungen',
};

export const CONFLICT: LegendEntry = {
    key: 'conflict', text: 'Konflikt', icon: 'mdi-alert', tone: 'bad',
    description: 'Lehrkraft, Raum oder Schüler:in ist zur gleichen Zeit doppelt eingeplant',
};

/** Legend of the calendar (status, log and marker icons) */
export function legendEntries(): LegendEntry[] {
    return [
        {key: 'substituted', text: 'Vertretung', icon: STATUS.substituted.icon, tone: 'purple', description: 'Eine andere Lehrkraft vertritt diese Stunde'},
        {key: 'vacant', text: 'Vertretung gesucht / unbesetzt', icon: STATUS.vacant.icon, tone: 'bad', description: 'Keine Lehrkraft eingeteilt'},
        {key: 'canceled', text: 'Ausfall', icon: STATUS.canceled.icon, tone: 'muted', description: 'Die Stunde fällt aus'},
        {key: 'moved', text: 'Verschoben', icon: STATUS.moved.icon, tone: 'muted', description: 'Die Stunde wurde einmalig auf einen anderen Termin verschoben'},
        {key: 'holiday', text: 'Ferien', icon: STATUS.holiday.icon, tone: 'warn', description: 'Fällt wegen Ferien oder Feiertag aus'},
        {key: 'draft', text: 'Entwurf', icon: STATUS.draft.icon, tone: 'muted', description: 'Kursentwurf, noch nicht aktiv'},
        // Lehrkraft-Bereich „Ich bin krank / kann nicht“ (same values as SICK_REPORT_LEGEND in teacherSickReportText.ts)
        {key: 'teacherReported', text: 'Ausfall gemeldet (Lehrkraft)', icon: 'mdi-account-alert-outline', tone: 'bad',
            description: 'Die Lehrkraft hat diese Stunde im Lehrkraft-Bereich als Ausfall gemeldet (krank / verhindert)'},
        {key: 'logged', text: 'protokolliert', icon: 'mdi-check', tone: 'good', description: 'Alle Protokolle eingetragen'},
        {key: 'missing', text: 'Protokoll fehlt', icon: 'mdi-alert-circle-outline', tone: 'bad', description: 'Stunde ist vorbei, Protokolle fehlen'},
        CONFLICT,
        OPEN_FOR_REGISTRATION,
    ];
}
